import React, { Component } from "react";
import { Steps, Tag, Modal, message, Spin } from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import axios from "axios";
import { connect } from "react-redux";
import moment from "moment";
import { ButtonItem } from "../../../../Components/Items";
import { claveFechaDia, fechaDiaParaPayload } from "../../../../Components/fmtFechaDia";
import PasoEjercicio from "./pasos/PasoEjercicio";
import PasoAplicacion from "./pasos/PasoAplicacion";
import PasoEmpleados from "./pasos/PasoEmpleados";
import PasoConceptos from "./pasos/PasoConceptos";
import PasoAplicar from "./pasos/PasoAplicar";
import ModalResetearAguinaldo from "./ModalResetearAguinaldo";
import ModalExcelAguinaldo from "./ModalExcelAguinaldo";
import { PASOS, fmtMXN, calcularImporte, filaIncluida, isrVigente, redondear2 } from "./aguinaldoUtils";

const PASO = { EJERCICIO: 0, APLICACION: 1, EMPLEADOS: 2, CONCEPTOS: 3, RESUMEN: 4 };

// Cambiar cualquiera de estos campos invalida la tabla de empleados: al volver al paso 3 se
// regenera la propuesta (conservando lo que el usuario editó a mano).
const CAMPOS_PROPUESTA = [
  "ejercicio", "fechaCorte", "descontarIncidencias", "tiposIncidenciaIds", "tipoAplicacion", "destinos",
];

const momentDia = (f) => {
  const k = claveFechaDia(f);
  return k ? moment(k, "YYYY-MM-DD") : null;
};

const finDeAnio = (anio) => moment([Number(anio), 11, 31]);

const estadoNuevo = () => {
  const anio = moment().year();
  return {
    id: null,
    estatus: "borrador",
    paso: PASO.EJERCICIO,
    pasoMaximo: PASO.EJERCICIO,
    ejercicio: anio,
    fechaCorte: finDeAnio(anio),
    descontarIncidencias: false,
    tiposIncidenciaIds: [],
    tipoAplicacion: "ordinaria",
    destinos: [],
    extraordinaria: { fechaPago: null, nombre: "" },
    empleados: [],
    diasAnio: 365,
    propuestaVigente: false,
    prenominas: [],
    destinoDescripcion: "",
    fechaAplicacion: null,
  };
};

const str = (v) => (v === null || v === undefined ? null : String(v));

// Proceso del servidor → estado de la pantalla.
const estadoDeProceso = (p) => ({
  id: p._id,
  estatus: p.estatus,
  paso: p.paso || 0,
  pasoMaximo: p.pasoMaximo || 0,
  ejercicio: p.ejercicio,
  fechaCorte: momentDia(p.fechaCorte) || finDeAnio(p.ejercicio),
  descontarIncidencias: !!p.descontarIncidencias,
  tiposIncidenciaIds: (p.tiposIncidenciaIds || []).map(String),
  tipoAplicacion: p.tipoAplicacion || "ordinaria",
  destinos: (p.destinos || []).map((d) => ({ tipoPeriodoId: str(d.tipoPeriodoId), prenominaId: str(d.prenominaId) })),
  extraordinaria: {
    fechaPago: momentDia(p.extraordinaria?.fechaPago),
    nombre: p.extraordinaria?.nombre || "",
  },
  empleados: (p.empleados || []).map((e) => ({
    ...e,
    empleadoId: String(e.empleadoId),
    prenominaId: str(e.prenominaId),
    tipoPeriodoId: str(e.tipoPeriodoId),
  })),
  diasAnio: p.diasAnio || 365,
  prenominas: p.prenominas || [],
  destinoDescripcion: p.destinoDescripcion || "",
  fechaAplicacion: p.fechaAplicacion || null,
});

/**
 * Asistente "Generar Aguinaldo" por pasos. El proceso se guarda como borrador al avanzar (y con
 * el botón Guardar) y se aplica sólo cuando el usuario lo decide en el último paso.
 */
class AsistenteAguinaldo extends Component {
  state = {
    ...estadoNuevo(),
    cargando: false,
    guardando: false,
    tiposIncidencia: [],
    gruposDestino: [],
    sinTipo: 0,
    cargandoDestinos: false,
    cargandoPropuesta: false,
    verificacion: null,
    cargandoVerificacion: false,
    asegurando: false,
    simulando: false,
    aplicando: false,
    deshaciendo: false,
    modalReset: false,
    modalExcel: false,
  };

  componentDidMount() {
    this.cargarTiposIncidencia();
    if (this.props.procesoId) this.cargarProceso(this.props.procesoId);
  }

  post(url, body) {
    const { empresaId, headersToken } = this.props;
    return axios.post(url, { empresaId, ...body }, { headers: headersToken });
  }

  get soloLectura() {
    return this.state.estatus === "aplicado";
  }

  // ─── Carga ─────────────────────────────────────────────────────────────────

  async cargarTiposIncidencia() {
    try {
      const res = await this.post("nomina/tiposIncidenciaAguinaldo", {});
      if (res.data.success === 2) this.setState({ tiposIncidencia: res.data.tipos });
    } catch {
      // Sólo alimenta el selector de incidencias; sin él, el resto del asistente funciona.
    }
  }

  async cargarProceso(id) {
    this.setState({ cargando: true });
    try {
      const res = await this.post("nomina/procesoAguinaldo", { id });
      if (res.data.success === 2) {
        const estado = estadoDeProceso(res.data.proceso);
        this.setState({ ...estado, propuestaVigente: estado.empleados.length > 0 }, () => this.entrarPaso(this.state.paso));
      } else {
        message.error(res.data.message || "No se pudo abrir el proceso");
      }
    } catch {
      message.error("Error de conexión al abrir el proceso");
    }
    this.setState({ cargando: false });
  }

  // ─── Guardar ───────────────────────────────────────────────────────────────

  payload(paso) {
    const s = this.state;
    return {
      id: s.id,
      ejercicio: s.ejercicio,
      fechaCorte: fechaDiaParaPayload(s.fechaCorte),
      diasAnio: s.diasAnio,
      descontarIncidencias: s.descontarIncidencias,
      tiposIncidenciaIds: s.tiposIncidenciaIds,
      tipoAplicacion: s.tipoAplicacion,
      // Con los vacíos: un tipo que el usuario dejó sin prenómina a propósito no se vuelve a llenar
      // con la sugerida al reabrir el borrador.
      destinos: s.destinos,
      extraordinaria: {
        fechaPago: fechaDiaParaPayload(s.extraordinaria.fechaPago),
        nombre: s.extraordinaria.nombre,
      },
      empleados: s.empleados,
      paso,
    };
  }

  /** Guarda el borrador. Devuelve true si quedó guardado. */
  async guardar(paso = this.state.paso, { avisar = false } = {}) {
    if (this.soloLectura) return true;
    this.setState({ guardando: true });
    let ok = false;
    try {
      const res = await this.post("nomina/guardarProcesoAguinaldo", this.payload(paso));
      if (res.data.success === 2) {
        const p = res.data.proceso;
        // Del servidor sólo se toma lo que él decide (id, paso alcanzado, nombres de destino):
        // lo capturado en pantalla ya es lo que se mandó.
        this.setState({
          id: p._id,
          pasoMaximo: p.pasoMaximo,
          prenominas: p.prenominas || [],
          destinoDescripcion: p.destinoDescripcion || "",
        });
        if (avisar) message.success("Borrador guardado");
        ok = true;
      } else {
        message.error(res.data.message || "No se pudo guardar el borrador");
      }
    } catch {
      message.error("Error de conexión al guardar el borrador");
    }
    this.setState({ guardando: false });
    return ok;
  }

  // ─── Navegación ────────────────────────────────────────────────────────────

  validarPaso(paso) {
    const s = this.state;
    if (paso === PASO.EJERCICIO) {
      if (!(s.ejercicio >= 2000 && s.ejercicio <= 2100)) return "Captura un ejercicio válido.";
      if (!s.fechaCorte) return "Captura la fecha de corte.";
      if (s.fechaCorte.year() !== Number(s.ejercicio)) return "La fecha de corte debe caer dentro del ejercicio.";
      if (s.descontarIncidencias && !s.tiposIncidenciaIds.length) {
        return "Elige qué tipos de incidencia se descuentan o desmarca la casilla.";
      }
    }
    if (paso === PASO.APLICACION) {
      if (s.tipoAplicacion === "ordinaria" && !s.destinos.some((d) => d.prenominaId)) {
        return "Elige la prenómina ordinaria de al menos un tipo de periodo.";
      }
      if (s.tipoAplicacion === "extraordinaria" && !s.extraordinaria.fechaPago) {
        return "Captura la fecha de pago del periodo extraordinario.";
      }
    }
    if (paso === PASO.EMPLEADOS) {
      if (s.cargandoPropuesta) return "Espera a que termine de calcularse la propuesta.";
      if (!s.empleados.some(filaIncluida)) return "Incluye al menos un empleado con importe mayor a cero.";
    }
    if (paso === PASO.CONCEPTOS) {
      if (!s.verificacion) return "Espera a que termine la revisión de conceptos.";
      if (s.verificacion.hayBloqueantes) return "Resuelve los pendientes de conceptos antes de continuar.";
    }
    return null;
  }

  // Un paso intermedio que hay que volver a ver antes de seguir: la tabla de empleados quedó
  // vieja (cambió el corte, las incidencias o el destino) o los conceptos no se han revisado.
  necesitaVisita(paso) {
    if (paso === PASO.EMPLEADOS) return !this.state.propuestaVigente;
    if (paso === PASO.CONCEPTOS) return !this.state.verificacion;
    return false;
  }

  async irA(destino) {
    const { paso, pasoMaximo } = this.state;
    if (destino === paso) return;
    if (destino > pasoMaximo + 1) return;
    if (destino > paso && !this.soloLectura) {
      // Para avanzar se validan todos los pasos intermedios, no sólo el actual, y el salto se
      // detiene en el primero que haya que volver a ver.
      for (let i = paso; i < destino; i++) {
        if (i > paso && this.necesitaVisita(i)) {
          destino = i;
          break;
        }
        const error = this.validarPaso(i);
        if (error) {
          message.warning(error);
          return;
        }
      }
    }
    const ok = await this.guardar(destino);
    if (!ok) return;
    this.setState({ paso: destino }, () => this.entrarPaso(destino));
  }

  entrarPaso(paso) {
    if (this.soloLectura) {
      if (paso === PASO.CONCEPTOS && !this.state.verificacion) this.verificar();
      return;
    }
    if (paso === PASO.APLICACION && this.state.tipoAplicacion === "ordinaria") this.cargarDestinos();
    if (paso === PASO.EMPLEADOS && (!this.state.propuestaVigente || !this.state.empleados.length)) this.cargarPropuesta();
    if (paso === PASO.CONCEPTOS) this.verificar();
  }

  // ─── Paso 1 y 2: cambios ───────────────────────────────────────────────────

  onCambio = (cambios) => {
    const extra = {};
    if ("ejercicio" in cambios && cambios.ejercicio) {
      // Si el corte seguía en el 31 de diciembre del ejercicio anterior, se mueve con él.
      const { fechaCorte, ejercicio } = this.state;
      if (!fechaCorte || fechaCorte.isSame(finDeAnio(ejercicio), "day")) extra.fechaCorte = finDeAnio(cambios.ejercicio);
    }
    if (cambios.descontarIncidencias && !this.state.tiposIncidenciaIds.length) {
      extra.tiposIncidenciaIds = this.state.tiposIncidencia.filter((t) => t.porDefecto).map((t) => String(t._id));
    }
    if (CAMPOS_PROPUESTA.some((k) => k in cambios)) extra.propuestaVigente = false;
    // La revisión de conceptos depende del corte (UMA vigente a esa fecha).
    if ("fechaCorte" in cambios || "ejercicio" in cambios) extra.verificacion = null;
    // La fecha de pago de la extraordinaria decide las tablas (UMA, tarifa) con que se grava: el
    // ISR que ya se había simulado deja de valer.
    if ("extraordinaria" in cambios) {
      const antes = fechaDiaParaPayload(this.state.extraordinaria.fechaPago);
      const despues = fechaDiaParaPayload(cambios.extraordinaria?.fechaPago);
      if (antes !== despues) {
        extra.empleados = this.state.empleados.map((e) => (e.simulacion ? { ...e, simulacion: undefined } : e));
      }
    }
    this.setState({ ...cambios, ...extra }, () => {
      if (cambios.tipoAplicacion === "ordinaria" && !this.state.gruposDestino.length) this.cargarDestinos();
    });
  };

  async cargarDestinos() {
    const { ejercicio, fechaCorte } = this.state;
    this.setState({ cargandoDestinos: true });
    try {
      const res = await this.post("nomina/prenominasDestinoAguinaldo", {
        ejercicio, fechaCorte: fechaDiaParaPayload(fechaCorte),
      });
      if (res.data.success === 2) {
        const grupos = res.data.grupos.map((g) => ({
          ...g,
          tipoPeriodoId: String(g.tipoPeriodoId),
          prenominas: g.prenominas.map((p) => ({ ...p, _id: String(p._id) })),
          sugeridaId: str(g.sugeridaId),
        }));
        // Destino por tipo: se respeta el elegido si sigue abierto (o si el usuario lo dejó vacío a
        // propósito); si nunca se eligió o ya se cerró, la sugerida.
        const destinos = grupos.map((g) => {
          const entrada = this.state.destinos.find((d) => d.tipoPeriodoId === g.tipoPeriodoId);
          if (entrada && !entrada.prenominaId) return { tipoPeriodoId: g.tipoPeriodoId, prenominaId: null };
          const sigueAbierta = entrada && g.prenominas.some((p) => p._id === entrada.prenominaId);
          return { tipoPeriodoId: g.tipoPeriodoId, prenominaId: sigueAbierta ? entrada.prenominaId : g.sugeridaId };
        });
        // Comparación sin importar el orden: sólo cuenta qué prenómina quedó en cada tipo.
        const firma = (lista) => lista
          .filter((d) => d.prenominaId)
          .map((d) => `${d.tipoPeriodoId}:${d.prenominaId}`)
          .sort()
          .join("|");
        const cambiaron = firma(destinos) !== firma(this.state.destinos);
        this.setState({
          gruposDestino: grupos,
          sinTipo: res.data.sinTipo || 0,
          destinos,
          ...(cambiaron ? { propuestaVigente: false } : {}),
        });
      } else {
        message.error(res.data.message || "No se pudieron cargar las prenóminas");
      }
    } catch {
      message.error("Error de conexión al cargar las prenóminas");
    }
    this.setState({ cargandoDestinos: false });
  }

  // ─── Paso 3: empleados ─────────────────────────────────────────────────────

  async cargarPropuesta() {
    const s = this.state;
    this.setState({ cargandoPropuesta: true });
    try {
      const res = await this.post("nomina/propuestaAguinaldo", {
        id: s.id,
        ejercicio: s.ejercicio,
        fechaCorte: fechaDiaParaPayload(s.fechaCorte),
        descontarIncidencias: s.descontarIncidencias,
        tiposIncidenciaIds: s.tiposIncidenciaIds,
        tipoAplicacion: s.tipoAplicacion,
        destinos: s.destinos.filter((d) => d.prenominaId),
        empleadosPrevios: s.empleados,
      });
      if (res.data.success === 2) {
        this.setState({
          empleados: res.data.empleados.map((e) => ({
            ...e,
            empleadoId: String(e.empleadoId),
            prenominaId: str(e.prenominaId),
            tipoPeriodoId: str(e.tipoPeriodoId),
          })),
          diasAnio: res.data.diasAnio || 365,
          propuestaVigente: true,
        });
      } else {
        message.error(res.data.message || "No se pudo calcular la propuesta");
      }
    } catch {
      message.error("Error de conexión al calcular la propuesta");
    }
    this.setState({ cargandoPropuesta: false });
  }

  onCambiarFila = (empleadoId, cambios, origen) => {
    this.setState((prev) => ({
      empleados: prev.empleados.map((e) => {
        if (e.empleadoId !== empleadoId) return e;
        const fila = { ...e, ...cambios, editado: true };
        if (origen === "incidencias") {
          // Corregir las incidencias rehace los días trabajados (ejercicio − incidencias) y, con
          // ellos, el importe por fórmula, igual que editar los días.
          fila.incidenciasManual = true;
          fila.diasTrabajados = Math.max(0, (Number(e.diasCalendario) || 0) - (Number(fila.diasIncidencias) || 0));
          fila.importe = calcularImporte(e.diasAguinaldo, prev.diasAnio, fila.diasTrabajados, e.salarioDiario);
          fila.importeManual = false;
        }
        if (origen === "dias") {
          // Editar los días regresa el importe a la fórmula.
          fila.importe = calcularImporte(e.diasAguinaldo, prev.diasAnio, fila.diasTrabajados, e.salarioDiario);
          fila.importeManual = false;
        }
        if (origen === "importe") fila.importeManual = true;
        return fila;
      }),
    }));
  };

  onRestaurarImporte = (empleadoId) => {
    this.setState((prev) => ({
      empleados: prev.empleados.map((e) => (e.empleadoId !== empleadoId ? e : {
        ...e,
        importe: calcularImporte(e.diasAguinaldo, prev.diasAnio, e.diasTrabajados, e.salarioDiario),
        importeManual: false,
        editado: true,
      })),
    }));
  };

  // "Marcar todos" no incluye a quien ya tiene aguinaldo: eso se decide renglón por renglón.
  onIncluirTodos = (incluir) => {
    this.setState((prev) => ({
      empleados: prev.empleados.map((e) => {
        if (e.noAplicable) return e;
        if (incluir && e.yaTieneAguinaldo) return e;
        return { ...e, incluido: incluir, editado: true };
      }),
    }));
  };

  // Resetear líneas y Excel trabajan sobre el borrador GUARDADO: se guarda antes de abrir para que
  // no se pierda lo editado en la tabla.
  abrirReset = async () => {
    if (await this.guardar(PASO.EMPLEADOS)) this.setState({ modalReset: true });
  };

  onResetAplicado = (proceso) => {
    this.setState({ empleados: estadoDeProceso(proceso).empleados, modalReset: false });
  };

  onExcelAplicado = async (empleados) => {
    this.setState({ empleados, modalExcel: false });
    await this.guardar(PASO.EMPLEADOS, { avisar: true });
  };

  // ─── Paso 4: conceptos ─────────────────────────────────────────────────────

  verificar = async () => {
    const { ejercicio, fechaCorte } = this.state;
    this.setState({ cargandoVerificacion: true });
    try {
      const res = await this.post("nomina/verificarConceptosAguinaldo", {
        ejercicio, fechaCorte: fechaDiaParaPayload(fechaCorte),
      });
      if (res.data.success === 2) this.setState({ verificacion: res.data });
      else message.error(res.data.message || "No se pudieron revisar los conceptos");
    } catch {
      message.error("Error de conexión al revisar los conceptos");
    }
    this.setState({ cargandoVerificacion: false });
  };

  asegurar = async () => {
    const { ejercicio, fechaCorte } = this.state;
    this.setState({ asegurando: true });
    try {
      const res = await this.post("nomina/asegurarConceptosAguinaldo", {
        ejercicio, fechaCorte: fechaDiaParaPayload(fechaCorte),
      });
      if (res.data.success === 2) {
        const { creados = [], corregidos = [] } = res.data;
        this.setState({ verificacion: res.data });
        if (creados.length || corregidos.length) {
          Modal.success({
            title: "Conceptos listos",
            content: (
              <div>
                {creados.length > 0 && (
                  <>
                    <b>Se dieron de alta:</b>
                    <ul>{creados.map((c) => <li key={c}>{c}</li>)}</ul>
                  </>
                )}
                {corregidos.length > 0 && (
                  <>
                    <b>Se corrigieron:</b>
                    <ul>{corregidos.map((c) => <li key={c}>{c}</li>)}</ul>
                  </>
                )}
              </div>
            ),
          });
        } else {
          message.info("No había nada que dar de alta o corregir.");
        }
      } else {
        message.error(res.data.message || "No se pudieron dar de alta los conceptos");
      }
    } catch {
      message.error("Error de conexión al dar de alta los conceptos");
    }
    this.setState({ asegurando: false });
  };

  // ─── Paso 5: ISR, imprimir, aplicar, deshacer ──────────────────────────────

  calcularIsr = async () => {
    if (!(await this.guardar(PASO.RESUMEN))) return false;
    this.setState({ simulando: true });
    let ok = false;
    try {
      const res = await this.post("nomina/simularIsrAguinaldo", { id: this.state.id });
      if (res.data.success === 2) {
        const estado = estadoDeProceso(res.data.proceso);
        this.setState({ empleados: estado.empleados });
        const omitidos = res.data.omitidos || [];
        if (omitidos.length) {
          message.warning(`${omitidos.length} empleado(s) sin ISR: ${omitidos[0].motivo}${omitidos.length > 1 ? "…" : ""}`);
        } else {
          message.success("ISR calculado");
        }
        ok = true;
      } else {
        message.error(res.data.message || "No se pudo calcular el ISR");
      }
    } catch {
      message.error("Error de conexión al calcular el ISR");
    }
    this.setState({ simulando: false });
    return ok;
  };

  imprimir = async () => {
    if (!this.soloLectura) {
      const faltaIsr = this.state.empleados.filter(filaIncluida).some((e) => !isrVigente(e));
      // Calcular el ISR ya guarda el borrador; si no hace falta, sólo se guarda.
      const ok = faltaIsr ? await this.calcularIsr() : await this.guardar(PASO.RESUMEN);
      if (!ok && !this.state.id) return;
    }
    this.props.onImprimir(this.state.id);
  };

  aplicar = () => {
    const s = this.state;
    const incluidos = s.empleados.filter(filaIncluida);
    const total = redondear2(incluidos.reduce((acc, e) => acc + (Number(e.importe) || 0), 0));
    const repetidos = incluidos.filter((e) => e.yaTieneAguinaldo).length;
    Modal.confirm({
      title: "Aplicar aguinaldo",
      okText: "Sí, aplicar",
      cancelText: "Cancelar",
      width: 520,
      content: (
        <div>
          <p>
            Se aplicará el aguinaldo {s.ejercicio} a <b>{incluidos.length}</b> empleado(s) por{" "}
            <b>{fmtMXN(total)}</b>.
          </p>
          {s.tipoAplicacion === "extraordinaria" ? (
            <p>
              Se creará un periodo extraordinario con fecha de pago{" "}
              <b>{s.extraordinaria.fechaPago?.format("DD/MM/YYYY")}</b> y su prenómina, que sólo calcula
              el aguinaldo y su ISR.
            </p>
          ) : (
            <p>Se agregará a los recibos de: <b>{s.destinoDescripcion}</b>, y se recalcularán.</p>
          )}
          {repetidos > 0 && (
            <p style={{ color: "#cf1322" }}>
              {repetidos} empleado(s) incluidos ya tienen aguinaldo de este ejercicio: se les pagaría otra vez.
            </p>
          )}
          <p className="aguinaldo-tenue">Podrás deshacerlo mientras no se timbre.</p>
        </div>
      ),
      onOk: () => this.confirmarAplicar(),
    });
  };

  async confirmarAplicar() {
    if (!(await this.guardar(PASO.RESUMEN))) return;
    this.setState({ aplicando: true });
    try {
      const res = await this.post("nomina/aplicarProcesoAguinaldo", { id: this.state.id });
      if (res.data.success === 2) {
        this.setState({ ...estadoDeProceso(res.data.proceso), propuestaVigente: true });
        const noAplicados = res.data.proceso.empleados.filter((e) => !e.aplicado && e.mensaje).length;
        if (noAplicados) message.warning(`Aplicado. ${noAplicados} empleado(s) no se pudieron aplicar: revisa la tabla.`);
        else message.success("Aguinaldo aplicado. La prenómina se está recalculando.");
      } else {
        message.error(res.data.message || "No se pudo aplicar el aguinaldo");
      }
    } catch {
      message.error("Error de conexión al aplicar el aguinaldo");
    }
    this.setState({ aplicando: false });
  }

  deshacer = () => {
    const s = this.state;
    Modal.confirm({
      title: "Deshacer aguinaldo",
      okText: "Sí, deshacer",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      content: s.tipoAplicacion === "extraordinaria"
        ? "Se eliminará la prenómina extraordinaria del aguinaldo (y su periodo). El proceso vuelve a borrador."
        : "Se quitará el aguinaldo de los recibos y se recalcularán las prenóminas. El proceso vuelve a borrador.",
      onOk: () => this.confirmarDeshacer(),
    });
  };

  async confirmarDeshacer() {
    this.setState({ deshaciendo: true });
    try {
      const res = await this.post("nomina/deshacerProcesoAguinaldo", { id: this.state.id });
      if (res.data.success === 2) {
        // Las filas conservan sus importes; la tabla se considera vigente salvo que cambie algo.
        this.setState({ ...estadoDeProceso(res.data.proceso), propuestaVigente: true });
        message.success("Aguinaldo deshecho. El proceso volvió a borrador.");
      } else {
        message.error(res.data.message || "No se pudo deshacer el aguinaldo");
      }
    } catch {
      message.error("Error de conexión al deshacer el aguinaldo");
    }
    this.setState({ deshaciendo: false });
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  nombrePrenomina = (id) => {
    if (!id) return "";
    const s = this.state;
    const p = s.prenominas.find((x) => String(x._id) === String(id))
      || s.gruposDestino.flatMap((g) => g.prenominas).find((x) => String(x._id) === String(id));
    return p?.nombre || "";
  };

  renderPaso() {
    const s = this.state;
    const soloLectura = this.soloLectura;
    switch (s.paso) {
      case PASO.EJERCICIO:
        return (
          <PasoEjercicio
            ejercicio={s.ejercicio}
            fechaCorte={s.fechaCorte}
            descontarIncidencias={s.descontarIncidencias}
            tiposIncidenciaIds={s.tiposIncidenciaIds}
            tiposIncidencia={s.tiposIncidencia}
            soloLectura={soloLectura}
            onCambio={this.onCambio}
          />
        );
      case PASO.APLICACION:
        return (
          <PasoAplicacion
            ejercicio={s.ejercicio}
            tipoAplicacion={s.tipoAplicacion}
            destinos={s.destinos}
            extraordinaria={s.extraordinaria}
            gruposDestino={s.gruposDestino}
            sinTipo={s.sinTipo}
            cargandoDestinos={s.cargandoDestinos}
            soloLectura={soloLectura}
            onCambio={this.onCambio}
          />
        );
      case PASO.EMPLEADOS:
        return (
          <PasoEmpleados
            empleados={s.empleados}
            tipoAplicacion={s.tipoAplicacion}
            descontarIncidencias={s.descontarIncidencias}
            nombrePrenomina={this.nombrePrenomina}
            cargando={s.cargandoPropuesta}
            soloLectura={soloLectura}
            diasAnio={s.diasAnio}
            ejercicio={s.ejercicio}
            fechaCorte={s.fechaCorte}
            onCambiarFila={this.onCambiarFila}
            onRestaurarImporte={this.onRestaurarImporte}
            onIncluirTodos={this.onIncluirTodos}
            onRecalcular={() => this.cargarPropuesta()}
            onResetear={this.abrirReset}
            onExcel={() => this.setState({ modalExcel: true })}
          />
        );
      case PASO.CONCEPTOS:
        return (
          <PasoConceptos
            verificacion={s.verificacion}
            cargando={s.cargandoVerificacion}
            asegurando={s.asegurando}
            soloLectura={soloLectura}
            onVerificar={this.verificar}
            onAsegurar={this.asegurar}
          />
        );
      default:
        return (
          <PasoAplicar
            ejercicio={s.ejercicio}
            fechaCorte={s.fechaCorte}
            diasAnio={s.diasAnio}
            tipoAplicacion={s.tipoAplicacion}
            destinoDescripcion={s.destinoDescripcion}
            empleados={s.empleados}
            estatus={s.estatus}
            fechaAplicacion={s.fechaAplicacion}
            nombrePrenomina={this.nombrePrenomina}
            simulando={s.simulando}
            aplicando={s.aplicando}
            deshaciendo={s.deshaciendo}
            onCalcularIsr={this.calcularIsr}
            onImprimir={this.imprimir}
            onAplicar={this.aplicar}
            onDeshacer={this.deshacer}
          />
        );
    }
  }

  render() {
    const s = this.state;
    const soloLectura = this.soloLectura;
    return (
      <Spin spinning={s.cargando}>
        <div className="aguinaldo-tarjeta">
          <div className="aguinaldo-asistente-cabecera">
            <ButtonItem icon={<ArrowLeftOutlined />} onClick={this.props.onSalir}>
              Procesos
            </ButtonItem>
            <div className="aguinaldo-titulo" style={{ margin: 0 }}>
              Aguinaldo {s.ejercicio}{" "}
              {soloLectura ? <Tag color="green">Aplicado</Tag> : <Tag color="gold">Borrador</Tag>}
            </div>
            <span />
          </div>

          <Steps
            current={s.paso}
            size="small"
            onChange={(i) => this.irA(i)}
            className="aguinaldo-steps"
          >
            {PASOS.map((p, i) => (
              <Steps.Step key={p.titulo} title={p.titulo} description={p.descripcion} disabled={i > s.pasoMaximo + 1} />
            ))}
          </Steps>

          {this.renderPaso()}

          <div className="aguinaldo-pie">
            <ButtonItem disabled={s.paso === 0} onClick={() => this.irA(s.paso - 1)}>
              Anterior
            </ButtonItem>
            <div style={{ display: "flex", gap: 8 }}>
              {!soloLectura && (
                <ButtonItem icon={<SaveOutlined />} loading={s.guardando} onClick={() => this.guardar(s.paso, { avisar: true })}>
                  Guardar
                </ButtonItem>
              )}
              {s.paso < PASO.RESUMEN && (
                <ButtonItem type="primary" loading={s.guardando} onClick={() => this.irA(s.paso + 1)}>
                  Siguiente
                </ButtonItem>
              )}
            </div>
          </div>
        </div>

        <ModalResetearAguinaldo
          visible={s.modalReset}
          procesoId={s.id}
          onCerrar={() => this.setState({ modalReset: false })}
          onAplicado={this.onResetAplicado}
        />
        <ModalExcelAguinaldo
          visible={s.modalExcel}
          empleados={s.empleados}
          diasAnio={s.diasAnio}
          ejercicio={s.ejercicio}
          tipoAplicacion={s.tipoAplicacion}
          descontarIncidencias={s.descontarIncidencias}
          nombrePrenomina={this.nombrePrenomina}
          empresaNombre={this.props.empresaNombre}
          onCerrar={() => this.setState({ modalExcel: false })}
          onAplicar={this.onExcelAplicado}
        />
      </Spin>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  empresaNombre: state.empresa.nombre,
});

export default connect(mapStateToProps)(AsistenteAguinaldo);
